import {Component, computed, DestroyRef, inject, input, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {disabled, form, required} from '@angular/forms/signals';
import {PreferredTimeOff} from '../../../../classes/models/employee/PreferredTimeOff';
import {DayOfWeek, dayOfWeekValues} from '../../../../classes/enums/DayOfWeek';
import {EditType} from '../../../../classes/enums/EditType';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {DataService} from '../../../../services/store/data.service';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {
  EmployeeWeeklyPreferredTimeOffService
} from '../../../../services/http/employee/employee-weekly-preferred-time-off.service';
import {
  applyCommonFieldsValidation,
  MAX_END_TIME,
  MIN_START_TIME,
  OngoingTimeOffFormModel, TimeOffFormCommonFieldsComponent
} from '../time-off-form-common-fields/time-off-form-common-fields.component';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * This component handles creating, editing and deleting of an ongoing preferred time off.
 */
@Component({
  selector: 'app-ongoing-time-off-form',
  templateUrl: './ongoing-time-off-form.component.html',
  styleUrls: ['./ongoing-time-off-form.component.less'],
  imports: [SHARED_IMPORTS, TimeOffFormCommonFieldsComponent]
})
export class OngoingTimeOffFormComponent implements OnInit {

  /** An edit action, which is performed by a user. */
  readonly editType = input<EditType>();
  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>();
  /** The date, starting from which weekly preferred times off will take effect. */
  readonly startDate = input<string>();
  /** The day of week, which is being edited by the user, or undefined if it is CREATE. */
  readonly dayOfWeek = input<DayOfWeek | undefined>();
  /** Ongoing preferred times off for the whole week. */
  readonly weeklyPreferredTimesOff = input<Map<DayOfWeek, PreferredTimeOff>>();
  /** Store earliest and latest working time for a week. */
  readonly storeWorkingTime = input<StoreWorkingTime>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = input<boolean>();
  /** A modal component of a preferred time off. */
  readonly modalComponent = input<ModalComponent>(undefined);
  readonly dataService = inject(DataService);
  /** A success message which should be showed when a request is successfully sent to backend. */
  readonly successMessage = computed(() => {
    const defaultMessage = 'Reoccurring preferred time off has been successfully ';
    return {
      [EditType.CREATE]: defaultMessage + 'created',
      [EditType.EDIT]: defaultMessage + 'edited',
      [EditType.DELETE]: defaultMessage + 'deleted'
    }[this.editType()];
  });
  /** Assigning an EditType, so it could be used in HTML. */
  EditType = EditType;
  /** All values of DayOfWeek. */
  weekdays = dayOfWeekValues();
  /** `True` if request sending to backend is in process. */
  readonly loading = signal(false);
  /** The first date from which a preferred time off is allowed to be edited. */
  readonly editDate = computed(() => this.dataService.editDate);
  /** Signal form model for an ongoing preferred time off. */
  readonly formModel = signal<OngoingTimeOffFormModel>({
    startDate: '',
    dayOfWeek: null,
    commonFields: {
      allDay: false,
      startTime: '',
      endTime: '',
      reason: ''
    }
  });
  /** Signal form with validation schema for an ongoing preferred time off. */
  readonly timeOffForm = form(this.formModel, (path) => {
    disabled(path.startDate);
    required(path.dayOfWeek);
    disabled(path.dayOfWeek, () => this.editType() === EditType.EDIT);
    applyCommonFieldsValidation(path.commonFields);
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly employeeWeeklyPreferredTimeOffService = inject(EmployeeWeeklyPreferredTimeOffService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);

  /**
   * Initializes the model from component inputs on page initialization.
   */
  ngOnInit(): void {
    const prevTimeOff = this.weeklyPreferredTimesOff()?.get(this.dayOfWeek());
    const openingTime = this.storeWorkingTime()?.openingTime ?? '';
    const closingTime = this.storeWorkingTime()?.closingTime ?? '';

    this.formModel.set({
      startDate: this.editDate(),
      dayOfWeek: this.dayOfWeek() ?? null,
      commonFields: {
        allDay: prevTimeOff?.startTime === MIN_START_TIME && prevTimeOff?.endTime === MAX_END_TIME,
        startTime: prevTimeOff?.startTime ?? openingTime,
        endTime: prevTimeOff?.endTime ?? closingTime,
        reason: prevTimeOff?.reason ?? ''
      }
    });
  }

  /**
   * Sends a request to backend to create or update a preferred time off. Sends edit request, if preferred time off week's data exists
   * in backend. Sends create request, if week's data does not exist in backend.
   * @param modalComponent a modal component of preferred time off.
   */
  onSubmit(modalComponent: ModalComponent) {
    this.loading.set(true);

    const preferredTimesOff: Map<DayOfWeek, PreferredTimeOff> = new Map(this.weeklyPreferredTimesOff());
    const formValue = this.formModel();
    const dayOfWeek = formValue.dayOfWeek;
    preferredTimesOff.delete(dayOfWeek);

    if (this.editType() !== EditType.DELETE) {
      const preferredTimeOff = new PreferredTimeOff(
              dayOfWeek,
              formValue.commonFields.reason,
              formValue.commonFields.startTime,
              formValue.commonFields.endTime,
              formValue.startDate
      );
      preferredTimesOff.set(dayOfWeek, preferredTimeOff);
    }

    if (this.weekExists()) {
      this.editPreferredTimeOff(modalComponent, preferredTimesOff);
    } else {
      this.addPreferredTimeOff(modalComponent, preferredTimesOff);
    }
  }

  /**
   * Checks if there is already existing record of the selected day of week.
   * @param dayOfWeek day of week option in the form.
   */
  dayOfWeekExists(dayOfWeek: DayOfWeek): boolean {
    return this.weeklyPreferredTimesOff()?.get(dayOfWeek) !== undefined;
  }

  private addPreferredTimeOff(modalComponent: ModalComponent, preferredTimesOff: Map<DayOfWeek, PreferredTimeOff>) {
    this.employeeWeeklyPreferredTimeOffService
            .addPreferredTimeOff(this.employeePositionId(), this.startDate(), preferredTimesOff)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success(this.successMessage());
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editPreferredTimeOff(modalComponent: ModalComponent, preferredTimesOff: Map<DayOfWeek, PreferredTimeOff>) {
    this.employeeWeeklyPreferredTimeOffService
            .editPreferredTimeOff(this.employeePositionId(), this.startDate(), preferredTimesOff)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success(this.successMessage());
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}